import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import AdminLayout from './layouts/AdminLayout';
import { RequirePermission } from '@features/auth/components/RequirePermission';

const DashboardPage        = lazy(() => import('./pages/DashboardPage'));
const AnalyticsPage        = lazy(() => import('./pages/AnalyticsPage'));
const BillingPage          = lazy(() => import('./pages/BillingPage'));
const PlatformBillingPage  = lazy(() => import('./pages/PlatformBillingPage'));
const PlatformPlansPage    = lazy(() => import('./pages/PlatformPlansPage'));
const PlatformTenantBillingPage = lazy(() => import('./pages/PlatformTenantBillingPage'));
const PlatformTenantDetailPage = lazy(() => import('./pages/PlatformTenantDetailPage'));
const TenantPlanBillingPage = lazy(() => import('./pages/TenantPlanBillingPage'));
const PlatformInvoicesPage = lazy(() => import('./pages/PlatformInvoicesPage'));
const KnowledgeGapsPage    = lazy(() => import('./pages/KnowledgeGapsPage'));
const KnowledgeAnswersPage = lazy(() => import('./pages/KnowledgeAnswersPage'));
const LiveCallsPage        = lazy(() => import('./pages/LiveCallsPage'));
const HandoffsPage         = lazy(() => import('./pages/HandoffsPage'));
const TicketsPage          = lazy(() => import('./pages/TicketsPage'));
const MeetingsPage         = lazy(() => import('./pages/MeetingsPage'));
const TenantsPage          = lazy(() => import('./pages/TenantsPage'));
const PlatformUsersPage    = lazy(() => import('./pages/PlatformUsersPage'));
const PlatformRolesPage    = lazy(() => import('./pages/PlatformRolesPage'));
const PlatformAuditPage    = lazy(() => import('./pages/PlatformAuditPage'));
const TenantUsersPage      = lazy(() => import('./pages/TenantUsersPage'));
const TenantRolesPage      = lazy(() => import('./pages/TenantRolesPage'));
const TenantAuditPage      = lazy(() => import('./pages/TenantAuditPage'));
const AgentsPage           = lazy(() => import('./pages/AgentsPage'));
const IntegrationsPage     = lazy(() => import('./pages/IntegrationsPage'));
const KnowledgeBasePage    = lazy(() => import('./pages/KnowledgeBasePage'));
const KnowledgePage        = lazy(() => import('./pages/KnowledgePage'));
const UploadDocumentsPage  = lazy(() => import('./pages/UploadDocumentsPage'));
const ConversationsPage    = lazy(() => import('./pages/ConversationsPage'));
const AgentActionsPage     = lazy(() => import('./pages/AgentActionsPage'));
const ConversationDetailPage = lazy(() => import('./pages/ConversationDetailPage'));
const SettingsPage         = lazy(() => import('./pages/SettingsPage'));
const CallLogsPage         = lazy(() => import('./pages/CallLogsPage'));
const PhoneNumbersPage     = lazy(() => import('./pages/PhoneNumbersPage'));
const RoutingPoliciesPage  = lazy(() => import('./pages/RoutingPoliciesPage'));
const PlatformVoiceEnginesPage = lazy(() => import('./pages/PlatformVoiceEnginesPage'));
const PlatformTelephonyCountriesPage = lazy(() => import('./pages/PlatformTelephonyCountriesPage'));
const PlatformPortRequestsPage = lazy(() => import('./pages/PlatformPortRequestsPage'));
const ProfilePage          = lazy(() => import('./pages/ProfilePage'));
const SecurityPage         = lazy(() => import('./pages/SecurityPage'));
const StyleGuidePage       = lazy(() => import('./pages/StyleGuidePage'));

export const adminRoutes: RouteObject[] = [
  {
    path: '/admin',
    element: <AdminLayout />,
    children: [
      {
        index: true,
        element: (
          <RequirePermission anyOf={['platform.dashboard.read']}>
            <DashboardPage />
          </RequirePermission>
        ),
      },
      {
        path: 'analytics',
        element: (
          <RequirePermission anyOf={['tenant.analytics_outcomes.read']}>
            <AnalyticsPage />
          </RequirePermission>
        ),
      },
      {
        path: 'billing',
        element: (
          <RequirePermission anyOf={['tenant.billing.read']}>
            <BillingPage />
          </RequirePermission>
        ),
      },
      {
        path: 'platform-billing',
        element: (
          <RequirePermission anyOf={['platform.billing.read']}>
            <PlatformBillingPage />
          </RequirePermission>
        ),
      },
      {
        path: 'platform-plans',
        element: (
          <RequirePermission anyOf={['platform.billing.read']}>
            <PlatformPlansPage />
          </RequirePermission>
        ),
      },
      {
        path: 'platform-tenant-billing',
        element: (
          <RequirePermission anyOf={['platform.billing.read']}>
            <PlatformTenantBillingPage />
          </RequirePermission>
        ),
      },
      {
        path: 'my-plan',
        element: (
          <RequirePermission anyOf={['tenant.billing.read']}>
            <TenantPlanBillingPage />
          </RequirePermission>
        ),
      },
      {
        path: 'platform-invoices',
        element: (
          <RequirePermission anyOf={['platform.billing.read']}>
            <PlatformInvoicesPage />
          </RequirePermission>
        ),
      },
      {
        path: 'knowledge-gaps',
        element: (
          <RequirePermission anyOf={['tenant.knowledge_gaps.read']}>
            <KnowledgeGapsPage />
          </RequirePermission>
        ),
      },
      {
        path: 'knowledge-answers',
        element: (
          <RequirePermission anyOf={['tenant.knowledge_answers.read']}>
            <KnowledgeAnswersPage />
          </RequirePermission>
        ),
      },
      {
        path: 'handoffs',
        element: (
          <RequirePermission anyOf={['tenant.handoffs.read']}>
            <HandoffsPage />
          </RequirePermission>
        ),
      },
      {
        path: 'tickets',
        element: (
          <RequirePermission anyOf={['tenant.tickets.read']}>
            <TicketsPage />
          </RequirePermission>
        ),
      },
      {
        path: 'agent-actions',
        element: (
          <RequirePermission anyOf={['tenant.agent_actions.read']}>
            <AgentActionsPage />
          </RequirePermission>
        ),
      },
      {
        path: 'meetings',
        element: (
          <RequirePermission anyOf={['tenant.meetings.read']}>
            <MeetingsPage />
          </RequirePermission>
        ),
      },
      {
        path: 'tenants',
        element: (
          <RequirePermission anyOf={['platform.tenants.read']}>
            <TenantsPage />
          </RequirePermission>
        ),
      },
      {
        // 2.9: tenant detail (Telephony tab).
        path: 'tenants/:tenantId',
        element: (
          <RequirePermission anyOf={['platform.tenants.read']}>
            <PlatformTenantDetailPage />
          </RequirePermission>
        ),
      },
      {
        path: 'platform-users',
        element: (
          <RequirePermission anyOf={['platform.platform_users.read']}>
            <PlatformUsersPage />
          </RequirePermission>
        ),
      },
      {
        path: 'platform-roles',
        element: (
          <RequirePermission anyOf={['platform.platform_roles.read']}>
            <PlatformRolesPage />
          </RequirePermission>
        ),
      },
      {
        path: 'platform-audit',
        element: (
          <RequirePermission anyOf={['platform.platform_audit.read']}>
            <PlatformAuditPage />
          </RequirePermission>
        ),
      },
      {
        path: 'users',
        element: (
          <RequirePermission anyOf={['tenant.tenant_users.read']}>
            <TenantUsersPage />
          </RequirePermission>
        ),
      },
      {
        path: 'roles',
        element: (
          <RequirePermission anyOf={['tenant.tenant_roles.read']}>
            <TenantRolesPage />
          </RequirePermission>
        ),
      },
      {
        path: 'audit',
        element: (
          <RequirePermission anyOf={['tenant.audit_log.read']}>
            <TenantAuditPage />
          </RequirePermission>
        ),
      },
      {
        path: 'agents',
        element: (
          <RequirePermission anyOf={['tenant.agents.read']}>
            <AgentsPage />
          </RequirePermission>
        ),
      },
      {
        path: 'integrations',
        element: (
          <RequirePermission anyOf={['tenant.integrations.view']}>
            <IntegrationsPage />
          </RequirePermission>
        ),
      },
      {
        path: 'knowledge',
        element: (
          <RequirePermission anyOf={['tenant.knowledge_documents.read']}>
            <KnowledgeBasePage />
          </RequirePermission>
        ),
      },
      {
        path: 'agents/:agentId/knowledge',
        element: (
          <RequirePermission anyOf={['tenant.knowledge_documents.read']}>
            <KnowledgePage />
          </RequirePermission>
        ),
      },
      { path: 'upload', element: <UploadDocumentsPage /> },
      {
        path: 'call-logs',
        element: (
          <RequirePermission anyOf={['tenant.call_logs.read']}>
            <CallLogsPage />
          </RequirePermission>
        ),
      },
      {
        path: 'phone-numbers',
        element: (
          <RequirePermission anyOf={['tenant.phone_numbers.read', 'tenant.telephony.read']}>
            <PhoneNumbersPage />
          </RequirePermission>
        ),
      },
      {
        path: 'live-calls',
        element: (
          <RequirePermission anyOf={['tenant.live_calls.read']}>
            <LiveCallsPage />
          </RequirePermission>
        ),
      },
      {
        path: 'routing-policies',
        element: (
          <RequirePermission anyOf={['tenant.routing_policies.read']}>
            <RoutingPoliciesPage />
          </RequirePermission>
        ),
      },
      {
        path: 'voice-engines',
        element: (
          <RequirePermission anyOf={['platform.telephony.read']}>
            <PlatformVoiceEnginesPage />
          </RequirePermission>
        ),
      },
      {
        // 4.3: purchase catalog per country (super admin).
        path: 'telephony-countries',
        element: (
          <RequirePermission anyOf={['platform.telephony.read']}>
            <PlatformTelephonyCountriesPage />
          </RequirePermission>
        ),
      },
      {
        // 4.3: porting-in queue.
        path: 'porting-requests',
        element: (
          <RequirePermission anyOf={['platform.telephony.read']}>
            <PlatformPortRequestsPage />
          </RequirePermission>
        ),
      },
      {
        path: 'agents/:agentId/conversations',
        element: (
          <RequirePermission anyOf={['tenant.conversations.read']}>
            <ConversationsPage />
          </RequirePermission>
        ),
      },
      {
        path: 'agents/:agentId/conversations/:sessionId',
        element: (
          <RequirePermission anyOf={['tenant.conversations.read']}>
            <ConversationDetailPage />
          </RequirePermission>
        ),
      },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'profile', element: <ProfilePage /> },
      { path: 'security', element: <SecurityPage /> },
      { path: 'style-guide', element: <StyleGuidePage /> },
    ],
  },
];
